import React, { memo, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import { pick } from 'ramda'

import { Config } from '@surglogs-app/types'
import { Button, Text } from '@surglogs-app/ui'
import { DialogLoader } from '@surglogs-app/ui.loader'
import { DefaultModal } from '@surglogs-app/ui.modal'
import { pinToString } from '@surglogs-app/util'

import { useAccountEditPinSubmitHandler } from '../hooks/useAccountEditPinSubmitHandler'
import { useSecurityPin } from '../hooks/useSecurityPin'
import { useAccountSettingsTranslation } from '../services'
import {
  AccountEditPinFormData,
  accountEditPinFormValidator,
} from '../validators/accountEditPinFormValidator'

import { AccountSettingsRecaptchaProvider } from './AccountSettingsRecaptchaProvider'
import { SettingsUserEditPinModalFormPassword } from './SettingsUserEditPinModalFormPassword'
import { SettingsUserEditPinModalFormPin } from './SettingsUserEditPinModalFormPin'
import { SettingsUserEditPinModalFormSubmitButton } from './SettingsUserEditPinModalFormSubmitButton'

interface SettingsUserEditPinModalProps {
  hideModal: () => void
  config: Pick<Config, 'reCaptchaKey'>
}

const AccountEditPinModalComponent = memo(
  ({ hideModal, config }: SettingsUserEditPinModalProps) => {
    const { pinLength, pinKeys } = useSecurityPin()
    const [submit, { isPending }] = useAccountEditPinSubmitHandler({
      hideModal,
      config,
    })

    const form = useForm<AccountEditPinFormData>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: yupResolver(accountEditPinFormValidator(pinKeys)),
    })

    const { isDirty } = form.formState

    const onSubmit = form.handleSubmit(async (data) => {
      await submit({
        password: data.password,
        pin: String(pinToString(pick(pinKeys, data))),
      })
    })

    const { t: ct } = useAccountSettingsTranslation()
    const footerChildren = useMemo(
      () => (
        <>
          <SettingsUserEditPinModalFormSubmitButton
            {...{ disabled: !isDirty }}
          />
          <Button {...{ variant: 'secondary', onClick: hideModal }}>
            {ct('revertChanges')}
          </Button>
        </>
      ),
      [ct, hideModal, isDirty],
    )

    return (
      <FormProvider {...form}>
        <form {...{ onSubmit, hideModal }}>
          <DefaultModal
            {...{
              title: ct('pinEditTitle'),
              onCancelButtonClick: null,
              footerChildren,
              hideModal,
            }}>
            {isPending ? <DialogLoader /> : null}
            <Text {...{ paragraph: true, mb: 'lg' }}>
              {ct('pinEditDescriptionText')}
            </Text>
            <SettingsUserEditPinModalFormPassword />
            <SettingsUserEditPinModalFormPin {...{ pinLength }} />
          </DefaultModal>
        </form>
      </FormProvider>
    )
  },
)

export const AccountEditPinModal = (props: SettingsUserEditPinModalProps) => (
  <AccountSettingsRecaptchaProvider {...{ config: props.config }}>
    <AccountEditPinModalComponent {...props} />
  </AccountSettingsRecaptchaProvider>
)
